/**
 * adaptiveOutput.ts — MER Algo Footprints v8.5.2
 *
 * Canonical post-engine adaptive output layer.
 *
 * ── Single responsibility ─────────────────────────────────────────────────────
 *
 * This is the ONE place where active adaptation is applied to derived outputs.
 * All consumers (Signals, Replay, Journal) call buildAdaptiveOutputs().
 * No other module calls applyConfidenceModifier() or applyWeightModifier() directly.
 *
 * ── Architecture position ────────────────────────────────────────────────────
 *
 *   Engines complete
 *       → base outputs assembled (viewModels/index.ts)
 *       → buildAdaptiveOutputs() ← THIS MODULE
 *           reads governance.currentModifiers + governance.mode
 *           applies bounded modifiers to allowed targets only
 *           returns adapted outputs + audit trail
 *       → consumers render adapted values
 *       → recordAuditEntry() called once per real application
 *
 * ── Safety guarantees ────────────────────────────────────────────────────────
 *
 * Advisory / shadow / frozen → base outputs returned unchanged, no audit emitted.
 * Active + flag ON → modifiers from governance applied, audit trail produced.
 * ALL modifiers bounded to [MIN_MODIFIER, MAX_MODIFIER].
 * MIN_SAMPLE_SIZE enforced (governance already validates at proposal time, this adds
 * defense-in-depth by checking profile.sampleSize before applying).
 *
 * ── Determinism ──────────────────────────────────────────────────────────────
 *
 * PURE FUNCTION — same (baseOutputs, govState) always produce same AdaptiveOutputResult.
 * No Date.now(), no I/O, no React/DOM dependencies.
 * Audit timestamps (from recordAuditEntry) are added by the CALLER, not here.
 *
 * ── Allowed adaptation targets ────────────────────────────────────────────────
 *
 *   ✓  confidence   — thesis/signal conviction scaling
 *   ✓  attribution  — signal layer weight adjustment (future)
 *   ✓  ranking      — signal priority scoring (future)
 *   ✓  warning_severity — alert weight emphasis (future)
 *
 *   ✗  gamma_math, liquidity_scoring, trap_classification,
 *      volatility_regime, structure_calc  — NEVER touched here
 */

import {
  isActiveAdaptationEnabled,
  applyConfidenceModifier,
} from './adaptiveApplication';
import type {
  SignalCalibrationProfile,
  RegimeKey,
  AdaptiveAuditEntry,
} from './adaptiveLearning';
import {
  ADAPTIVE_LEARNING_THRESHOLDS as T,
} from './adaptiveLearning';

// ── Input / output contracts ──────────────────────────────────────────────────

/**
 * Base post-engine outputs that may be adapted.
 * All fields are optional — pass only what the caller has available.
 * Non-adapted fields flow through unchanged.
 */
export interface AdaptiveBaseOutputs {
  /** Thesis/signal conviction (0–100) */
  conviction?:    number | null;
  /** Regime confidence (0–100) — future adaptation target */
  regimeConf?:    number | null;
  /** Attribution layer weights — future adaptation target */
  weights?:       Record<string, number>;
}

/**
 * Result of buildAdaptiveOutputs().
 * Mirrors the shape of AdaptiveBaseOutputs with adapted values where applicable.
 */
export interface AdaptiveOutputResult {
  /** Adapted conviction — equals baseOutputs.conviction when no modifier applied */
  conviction:     number | null;
  /** Adapted regime confidence — currently passes through unchanged */
  regimeConf:     number | null;
  /** Adapted attribution weights — currently passes through unchanged */
  weights:        Record<string, number>;
  /** True when at least one modifier was applied */
  anyModified:    boolean;
  /**
   * Audit records for all applied modifiers.
   * Empty when advisory/shadow/frozen or no modifier fires.
   * Caller is responsible for passing these to recordAuditEntry().
   */
  auditEntries:   AdaptiveAuditEntry[];
}

// ── Governance-to-calibProfile bridge ────────────────────────────────────────
//
// Converts the governance currentModifiers['calib:global'] entry into the
// SignalCalibrationProfile shape that applyConfidenceModifier() needs.
// Returns null when no active confidence modifier exists.

export function calibProfileFromGovernance(
  currentModifiers: Record<string, number>,
): SignalCalibrationProfile | null {
  const mod = currentModifiers['calib:global'];
  if (mod == null) return null;
  if (Math.abs(mod - T.NEUTRAL_MODIFIER) < 0.001) return null;

  const bias = mod < T.NEUTRAL_MODIFIER ? 'overconfident' : 'underconfident';
  return {
    signalKey:            'global',
    sampleSize:           T.MIN_SAMPLE_SIZE,   // governance validated at proposal time
    overconfidentCount:   bias === 'overconfident' ? 1 : 0,
    underconfidentCount:  bias === 'underconfident' ? 1 : 0,
    wellCalibratedCount:  0,
    confidenceBias:       bias,
    suggestedAdjustment:  mod,
    explanation:          `Governance-approved modifier ×${mod.toFixed(3)}`,
  };
}

// ── Canonical application function ───────────────────────────────────────────

/**
 * Apply governance-controlled adaptive modifiers to post-engine outputs.
 *
 * PURE FUNCTION — deterministic, no I/O, no React dependencies.
 *
 * @param base         — base post-engine outputs (not mutated)
 * @param currentMods  — governance.currentModifiers
 * @param govMode      — governance.mode ('shadow'|'advisory'|'active')
 * @param regimeKey    — structured regime context for audit records
 * @returns            AdaptiveOutputResult — adapted values + audit trail
 */
export function buildAdaptiveOutputs(
  base:        AdaptiveBaseOutputs,
  currentMods: Record<string, number>,
  govMode:     'shadow' | 'advisory' | 'active',
  regimeKey:   RegimeKey,
): AdaptiveOutputResult {
  const passthrough: AdaptiveOutputResult = {
    conviction:  base.conviction   ?? null,
    regimeConf:  base.regimeConf   ?? null,
    weights:     { ...(base.weights ?? {}) },
    anyModified: false,
    auditEntries: [],
  };

  // Guard 1: feature flag must be explicitly on
  if (!isActiveAdaptationEnabled()) return passthrough;

  // Guard 2: governance mode must be 'active' (shadow/advisory = no-op)
  if (govMode !== 'active') return passthrough;

  const auditEntries: AdaptiveAuditEntry[] = [];
  let conviction   = base.conviction   ?? null;
  let anyModified  = false;

  // ── Apply confidence calibration modifier ─────────────────────────────────
  if (conviction != null) {
    const calibProfile = calibProfileFromGovernance(currentMods);
    if (calibProfile !== null) {
      const result = applyConfidenceModifier(conviction, calibProfile, regimeKey);
      if (result.modifierApplied && result.auditEntry != null) {
        conviction   = result.adjustedValue;
        anyModified  = true;
        auditEntries.push(result.auditEntry);
      }
    }
  }

  // ── Future: attribution weight modifiers ──────────────────────────────────
  // When weight modifiers land, they slot in here:
  //   const weights = applyWeightModifiers(base.weights, currentMods, regimeKey, auditEntries);
  // For now, weights pass through unchanged.
  const weights = { ...(base.weights ?? {}) };

  return {
    conviction,
    regimeConf:  base.regimeConf ?? null,
    weights,
    anyModified,
    auditEntries,
  };
}
